/**
 * DealSidebar — Sidebar content with Chats / Deals tabs.
 * Wraps the existing conversation list and adds a deals tab.
 *
 * Features:
 * - Search/filter conversations
 * - Date-grouped conversation list (Today, Yesterday, This Week, etc.)
 * - Deals tab with expandable linked conversations
 * - Deal badge on linked conversations
 * - New Deal creation form
 * - Deal status filters
 */

import { useState, useEffect, useMemo } from "react";
import { useDeal } from "@/context/DealContext";
import { getDealConversations } from "@/api/client";
import DealListItem from "@/components/chat/DealListItem";
import CreateDealForm from "@/components/chat/CreateDealForm";
import DealStageBadge from "@/components/shared/DealStageBadge";
import type { Conversation } from "@/api/types";
import type { DealData } from "@/api/client";

interface DealSidebarProps {
  conversations: Conversation[];
  currentConvoId: string | null;
  onOpenConversation: (convo: Conversation) => void;
  onDeleteConversation: (id: string) => void;
  onNewChat: () => void;
  onCreateDeal?: (deal: DealData, message: string) => void;
  onOpenDealDetail?: (dealId: string) => void;
}

// ── Date grouping helpers ────────────────────────────────────

function getDateGroup(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date(today);
  monthAgo.setMonth(monthAgo.getMonth() - 1);

  if (date >= today) return "Today";
  if (date >= yesterday) return "Yesterday";
  if (date >= weekAgo) return "This Week";
  if (date >= monthAgo) return "This Month";
  return "Older";
}

const GROUP_ORDER = ["Today", "Yesterday", "This Week", "This Month", "Older"];

function groupConversations(
  conversations: Conversation[]
): Array<{ label: string; items: Conversation[] }> {
  const groups = new Map<string, Conversation[]>();
  for (const c of conversations) {
    const group = getDateGroup(c.updated_at || c.created_at);
    const arr = groups.get(group);
    if (arr) {
      arr.push(c);
    } else {
      groups.set(group, [c]);
    }
  }
  return GROUP_ORDER.filter((g) => groups.has(g)).map((g) => ({
    label: g,
    items: groups.get(g)!,
  }));
}

// ── Search icon SVG ──────────────────────────────────────────

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// ── Plus icon SVG ────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

// ── Status filter options ────────────────────────────────────

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "screening", label: "Screening" },
  { value: "diligence", label: "Diligence" },
  { value: "closed", label: "Closed" },
] as const;

// ── Component ────────────────────────────────────────────────

export default function DealSidebar({
  conversations,
  currentConvoId,
  onOpenConversation,
  onDeleteConversation,
  onNewChat,
  onCreateDeal,
  onOpenDealDetail,
}: DealSidebarProps) {
  const [tab, setTab] = useState<"chats" | "deals">("chats");
  const [search, setSearch] = useState("");
  const { deals, activeDeal, setActiveDeal, updateDeal } = useDeal();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creatingDeal, setCreatingDeal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Deal conversations expansion state
  const [expandedDealId, setExpandedDealId] = useState<string | null>(null);
  const [dealConvos, setDealConvos] = useState<Conversation[]>([]);
  const [loadingConvos, setLoadingConvos] = useState(false);

  // Build a deal_id → deal lookup for the chats tab badges
  const dealById = new Map(deals.map((d) => [d.id, d]));

  // Filter and group conversations
  const filteredConversations = useMemo(() => {
    if (!search.trim()) return conversations;
    const q = search.toLowerCase();
    return conversations.filter(
      (c) =>
        c.title?.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q)
    );
  }, [conversations, search]);

  const grouped = useMemo(
    () => groupConversations(filteredConversations),
    [filteredConversations]
  );

  // Filter deals by status
  const filteredDeals = useMemo(() => {
    if (statusFilter === "all") return deals;
    if (statusFilter === "closed") {
      return deals.filter((d) => d.status === "closed_won" || d.status === "closed_lost");
    }
    return deals.filter((d) => d.status === statusFilter);
  }, [deals, statusFilter]);

  // Load conversations when a deal is expanded
  useEffect(() => {
    if (!expandedDealId) {
      setDealConvos([]);
      return;
    }
    let cancelled = false;
    setLoadingConvos(true);
    getDealConversations(expandedDealId).then((convos) => {
      if (!cancelled) {
        setDealConvos(convos);
        setLoadingConvos(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [expandedDealId]);

  const handleDealClick = (deal: DealData) => {
    setActiveDeal(deal);
    setExpandedDealId((prev) => (prev === deal.id ? null : deal.id));
  };

  const handleDealUpdate = (updated: DealData) => {
    // Propagate update through context
    updateDeal(updated.id, {
      status: updated.status as DealData["status"],
      title: updated.title,
      notes: updated.notes,
    });
  };

  const handleCreateDeal = async (params: { address: string; intended_use?: string }) => {
    if (!onCreateDeal) return;
    setCreatingDeal(true);
    try {
      const { createDeal } = await import("@/api/client");
      const deal = await createDeal({
        address: params.address,
        intended_use: params.intended_use,
      });
      if (deal) {
        const useLabel = params.intended_use ? ` for ${params.intended_use}` : "";
        onCreateDeal(deal, `Analyze ${params.address}${useLabel}`);
        setShowCreateForm(false);
      }
    } finally {
      setCreatingDeal(false);
    }
  };

  return (
    <>
      {/* New Chat + Search */}
      <div style={{ padding: "0 0 4px" }}>
        <button className="btn-new-chat" onClick={onNewChat}>
          <PlusIcon />
          New Chat
        </button>
        <div className="sidebar-search">
          <div className="sidebar-search-wrap">
            <span className="sidebar-search-icon">
              <SearchIcon />
            </span>
            <input
              type="text"
              className="sidebar-search-input"
              placeholder="Search conversations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sidebar-tabs">
        <button
          className={`sidebar-tab ${tab === "chats" ? "active" : ""}`}
          onClick={() => setTab("chats")}
        >
          Chats
          {conversations.length > 0 && (
            <span className="sidebar-tab-count">{conversations.length}</span>
          )}
        </button>
        <button
          className={`sidebar-tab ${tab === "deals" ? "active" : ""}`}
          onClick={() => setTab("deals")}
        >
          Deals
          {deals.length > 0 && (
            <span className="sidebar-tab-count">{deals.length}</span>
          )}
        </button>
      </div>

      {/* Content */}
      {tab === "chats" ? (
        <div className="conversation-list">
          {grouped.length === 0 && search.trim() ? (
            <div
              style={{
                padding: "20px 16px",
                color: "var(--text-dim)",
                fontSize: "13px",
                textAlign: "center",
              }}
            >
              No conversations matching &ldquo;{search}&rdquo;
            </div>
          ) : grouped.length === 0 ? (
            <div
              style={{
                padding: "20px 16px",
                color: "var(--text-dim)",
                fontSize: "13px",
                textAlign: "center",
              }}
            >
              No conversations yet
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.label}>
                <div className="convo-date-group">{group.label}</div>
                {group.items.map((c) => {
                  const linkedDeal = c.deal_id
                    ? dealById.get(c.deal_id)
                    : null;
                  return (
                    <div
                      key={c.id}
                      className={`convo-item ${
                        currentConvoId === c.id ? "active" : ""
                      }`}
                      onClick={() => onOpenConversation(c)}
                    >
                      <div className="convo-item-content">
                        <span className="convo-title">{c.title}</span>
                        {linkedDeal && (
                          <DealStageBadge stage={linkedDeal.status} />
                        )}
                      </div>
                      <button
                        className="convo-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteConversation(c.id);
                        }}
                        aria-label="Delete conversation"
                      >
                        &times;
                      </button>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="conversation-list">
          {/* New Deal button */}
          {!showCreateForm && (
            <button
              className="btn-new-deal"
              onClick={() => setShowCreateForm(true)}
            >
              <PlusIcon />
              New Deal
            </button>
          )}

          {/* Create Deal form */}
          {showCreateForm && (
            <CreateDealForm
              onCreated={handleCreateDeal}
              onCancel={() => setShowCreateForm(false)}
              submitting={creatingDeal}
            />
          )}

          {/* Status filters */}
          <div className="deal-status-filters">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                className={`deal-filter-chip ${statusFilter === f.value ? "active" : ""}`}
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {filteredDeals.length === 0 ? (
            <div
              style={{
                padding: "24px 16px",
                color: "var(--text-dim)",
                fontSize: "13px",
                textAlign: "center",
                lineHeight: "1.6",
              }}
            >
              {statusFilter !== "all" ? (
                <>No {statusFilter} deals.</>
              ) : (
                <>
                  No deals yet.
                  <br />
                  Click &ldquo;New Deal&rdquo; or mention a property to start one.
                </>
              )}
            </div>
          ) : (
            filteredDeals.map((deal) => (
              <div key={deal.id}>
                <DealListItem
                  deal={deal}
                  isActive={activeDeal?.id === deal.id}
                  onClick={() => handleDealClick(deal)}
                  onUpdate={handleDealUpdate}
                  onOpenDetail={() => onOpenDealDetail?.(deal.id)}
                />
                {/* Expanded conversations under selected deal */}
                {expandedDealId === deal.id && (
                  <div className="deal-expansion">
                    {loadingConvos ? (
                      <div className="deal-expansion-empty">
                        <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} />{" "}
                        Loading...
                      </div>
                    ) : dealConvos.length === 0 ? (
                      <div className="deal-expansion-empty">
                        No conversations yet
                      </div>
                    ) : (
                      dealConvos.map((c) => (
                        <div
                          key={c.id}
                          className={`convo-item ${
                            currentConvoId === c.id ? "active" : ""
                          }`}
                          onClick={() => onOpenConversation(c)}
                        >
                          <span className="convo-title">{c.title}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
}
